import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "./baseQuery";

const buildQuery = (path, params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }
    searchParams.set(key, String(value));
  });

  const queryString = searchParams.toString();
  return queryString ? `${path}?${queryString}` : path;
};

const unwrap = (res) => (res?.success ? res.data : res ?? null);

/**
 * Customers area (private admin data). Orders keeps its own lightweight
 * customer autocomplete endpoint; this API is the dedicated Customers module.
 */
export const customersApi = createApi({
  reducerPath: "customersApi",
  baseQuery,
  tagTypes: ["Customers", "Customer"],

  endpoints: (builder) => ({
    fetchCustomers: builder.query({
      query: (params = {}) => buildQuery("customers", params),
      transformResponse: unwrap,
      providesTags: [{ type: "Customers", id: "LIST" }],
    }),

    fetchCustomerSummary: builder.query({
      query: () => "customers/summary",
      transformResponse: unwrap,
      providesTags: [{ type: "Customers", id: "SUMMARY" }],
    }),

    fetchCustomerCities: builder.query({
      query: () => "customers/cities",
      transformResponse: (res) => unwrap(res)?.items || [],
      providesTags: [{ type: "Customers", id: "CITIES" }],
    }),

    fetchCustomer: builder.query({
      query: (customerId) => `customers/${customerId}`,
      transformResponse: unwrap,
      providesTags: (_result, _error, customerId) => [{ type: "Customer", id: customerId }],
    }),

    fetchCustomerOrders: builder.query({
      query: ({ customerId, ...params }) => buildQuery(`customers/${customerId}/orders`, params),
      transformResponse: unwrap,
      providesTags: (_result, _error, arg) => [{ type: "Customer", id: arg.customerId }],
    }),

    fetchCustomerAddresses: builder.query({
      query: (customerId) => `customers/${customerId}/addresses`,
      transformResponse: (res) => unwrap(res)?.items || [],
      providesTags: (_result, _error, customerId) => [{ type: "Customer", id: customerId }],
    }),

    fetchPossibleDuplicates: builder.query({
      query: ({ customerId, phone }) =>
        buildQuery(`customers/${customerId}/possible-duplicates`, { phone }),
      transformResponse: (res) => unwrap(res)?.items || [],
    }),

    updateCustomer: builder.mutation({
      query: ({ customerId, ...body }) => ({
        url: `customers/${customerId}`,
        method: "PATCH",
        body,
      }),
      transformResponse: unwrap,
      invalidatesTags: (_result, _error, arg) => [
        { type: "Customer", id: arg.customerId },
        { type: "Customers", id: "LIST" },
        { type: "Customers", id: "SUMMARY" },
        { type: "Customers", id: "CITIES" },
      ],
    }),
  }),
});

export const {
  useFetchCustomersQuery,
  useFetchCustomerSummaryQuery,
  useFetchCustomerCitiesQuery,
  useFetchCustomerQuery,
  useFetchCustomerOrdersQuery,
  useFetchCustomerAddressesQuery,
  useLazyFetchPossibleDuplicatesQuery,
  useUpdateCustomerMutation,
} = customersApi;
